import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import statusConfig from '@/../config/status.json';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { cn } from '@/lib/utils';
import { getCurrentPremiumRatePercentage } from '@/services/utils';
import DescriptionListItem from './DescriptionListItem';
import EditLink from './EditLink';
import PolicyDocumentUpload from './PolicyDocumentUpload';

interface Customer {
	company_name?: string;
	coc?: string;
	sector_name?: string;
	address?: string;
	zip_code?: string;
	city?: string;
}

interface SidebarProps {
	onEditPremiumPercentage?: () => void;
	onEditSectorCode?: () => void;
	handleTriggerAppStatePolling?: () => void;
	/** Renders the bordered white card look used on the desktop `<aside>`. The
	 *  mobile drawer already provides its own full-bleed surface, so it passes
	 *  `false` to show the same content without a nested card-in-a-card look. */
	showCard?: boolean;
}

const Sidebar = ({
	onEditPremiumPercentage,
	onEditSectorCode,
	handleTriggerAppStatePolling,
	showCard = true
}: SidebarProps) => {
	const { t } = useTranslation();
	const { appData } = useAppContext();
	const slstPolicy = appData?.current_policy?.sick_leave_short_term;
	const providerName = slstPolicy?.insurer_name;
	const premiumRate = getCurrentPremiumRatePercentage(appData);
	const customer = appData?.company_info as Customer | undefined;
	const emptyValue =
		appData?.status && statusConfig.groups.isComplete.includes(appData.status) ? '-' : null;
	// No-policy / initial-payload start: show the "add your policy PDF later" CTA
	// above the premium row under "Current Policy" so the user can upload it later to
	// backfill current premium & coverage (issue #961). We gate on whether a
	// current-policy document exists on the form — NOT on parsed insurer/coverage:
	// a start-upload form has no parsed policy while it's still extracting (and a
	// manually-typed premium never produces one), so a data-presence proxy would
	// wrongly show the CTA there. `has_current_policy_document` is the backend's
	// flow signal — false only for fileless no-policy / initial-payload forms — so
	// it hides cleanly for the start-upload flow (even mid-extraction) and right
	// after a later upload, while still showing for a no-policy user who only typed
	// a premium. Gated on loaded data so it never flashes during initial load.
	const baseShowPolicyUpload = !!appData && !appData.has_current_policy_document;
	// Disable the upload while quotes are being (re)fetched (e.g. a concurrent
	// premium/coverage/sector recompute) so a new extraction can't race an
	// in-flight quote fetch. Re-enabled once the offer settles.
	const isFetchingQuotes =
		!!appData?.status && statusConfig.groups.pendingQuotes.includes(appData.status);
	// Keep the upload widget mounted (its in-place spinner) through the *extraction*
	// half of the recompute an add-later upload triggers. `has_current_policy_document`
	// flips true the instant the file is stored (while it's still extracting) and back
	// to false only if the document is rejected; without this latch the widget would
	// unmount for the whole window and only reappear — with the error — once the
	// backend settles, leaving a visible gap. Latching on "was already showing" scopes
	// this to the no-policy flow: a start-upload user (flag always true) never shows it.
	//
	// Success signal: the backend clears the stale quotes and pushes a snapshot
	// carrying the freshly parsed `current_policy` (its SLST coverage) the moment
	// quote-fetching begins, before any new quote lands. A rejection never parses SLST
	// coverage (and re-shows the widget as the error), so keying on parsed coverage
	// cleanly distinguishes success from failure.
	const hasParsedCurrentPolicy = !!slstPolicy?.coverage;
	const wasShowingUploadRef = useRef(baseShowPolicyUpload);
	useEffect(() => {
		if (!isFetchingQuotes) wasShowingUploadRef.current = baseShowPolicyUpload;
	}, [isFetchingQuotes, baseShowPolicyUpload]);
	// An add-later upload was read successfully: parsed coverage arrived while we were
	// mid-fetch after showing the upload widget. Surface a green confirmation instead
	// of silently unmounting.
	const uploadSucceeded = wasShowingUploadRef.current && isFetchingQuotes && hasParsedCurrentPolicy;
	// Keep the widget mounted through the whole quote-fetch window an add-later upload
	// triggers — first its extraction spinner, then (on success) the green confirmation
	// — rather than handing off the instant coverage parses. It unmounts once the quotes
	// are ready and the current-policy card below takes over.
	const showPolicyUpload =
		baseShowPolicyUpload || (wasShowingUploadRef.current && isFetchingQuotes);
	// Show the insurer only once there is one to show. Default to hidden — a
	// no-policy customer never gets an insurer (including after they enter a
	// rate, which the backend stores on `current_policy`, so we can't key off
	// the policy object existing). The field appears if/when an insurer name
	// arrives, rather than flashing an empty skeleton during load and then
	// vanishing.
	const showProvider = Boolean(providerName);

	return (
		<div className="h-full select-none">
			<div
				className={cn(
					'w-full h-full',
					showCard ? 'border border-gray-200 rounded-lg p-8 bg-white' : 'px-2 py-1'
				)}
			>
				<div className="relative">
					<AnimatePresence initial={false}>
						{showPolicyUpload && (
							<motion.div
								key="policy-upload"
								className="mb-8 overflow-hidden"
								exit={{ opacity: 0, height: 0, marginBottom: 0 }}
								transition={{ duration: 0.25, ease: 'easeInOut' }}
							>
								<PolicyDocumentUpload
									handleTriggerAppStatePolling={handleTriggerAppStatePolling}
									unreadableReason={appData?.unreadable_reason}
									disabled={isFetchingQuotes}
									succeeded={uploadSucceeded}
								/>
							</motion.div>
						)}
					</AnimatePresence>
					<h4 className="text-primary text-lg font-bold mb-4">
						{t('quotes.sidebar.currentPolicy')}
					</h4>
					{showProvider && (
						<DescriptionListItem title={t('quotes.sidebar.provider')} value={providerName} />
					)}
					<div className="mb-4">
						<dt className="text-sm font-bold text-muted-foreground mb-2">
							{onEditPremiumPercentage ? (
								<button
									type="button"
									onClick={onEditPremiumPercentage}
									className="inline-flex items-center gap-2 group cursor-pointer font-bold text-muted-foreground hover:text-foreground transition-colors"
									data-testid="edit-premium-percentage-button"
								>
									{t('quotes.sidebar.premiumRate')}
									<EditLink />
								</button>
							) : (
								t('quotes.sidebar.premiumRate')
							)}
						</dt>
						<dd className="font-bold text-foreground">
							{premiumRate ? (
								<span>{`${premiumRate}%`}</span>
							) : emptyValue != null ? (
								<span>{emptyValue}</span>
							) : (
								<div className="animate-pulse opacity-100 h-4 bg-muted rounded w-20" />
							)}
						</dd>
					</div>

					<h6 className="text-primary text-lg font-bold mb-4 mt-8">
						{t('quotes.sidebar.companyInfo')}
					</h6>
					<DescriptionListItem
						title={t('quotes.sidebar.companyName')}
						value={customer?.company_name}
					/>
					<DescriptionListItem title={t('quotes.sidebar.cocNumber')} value={customer?.coc} />
					<DescriptionListItem
						title={t('quotes.sidebar.sector')}
						value={customer?.sector_name}
						onEdit={onEditSectorCode}
						editTestId="edit-sector-code-button"
					/>
					<DescriptionListItem
						title={t('quotes.sidebar.address')}
						value={
							!customer?.address && !customer?.zip_code && !customer?.city
								? undefined
								: [customer?.address, customer?.zip_code, customer?.city].filter(
										(val): val is string => Boolean(val)
									)
						}
					/>
				</div>
			</div>
		</div>
	);
};

export default Sidebar;
